import { Helmet } from "react-helmet-async";
import { PAGE_BY_PATH } from "@/lib/seo/pages";

const BASE = "https://vanturis.com";

/**
 * Unified <head> for every public preview page.
 *
 * Ensures unique title, description, canonical URL, Open Graph + Twitter
 * cards, and (optionally) one or more JSON-LD blocks (WebPage,
 * BreadcrumbList, FAQ, Product/Vehicle).
 *
 * Usage:
 *   <SEO path="/prevost-motorhomes" jsonLd={[webPageLd, breadcrumbLd]} />
 *
 * If a `path` is registered in `/lib/seo/pages.js`, its canonical title,
 * description, and indexability are used as defaults.  Callers can still
 * override any field explicitly.  Indexable defaults to `false`
 * (belt-and-braces during coming_soon) unless the page manifest says
 * otherwise or the caller passes `noIndex={false}`.
 *
 * `exactTitle=true` uses `title` verbatim — the homepage uses this so
 * the Google-indexing brand title is not suffixed with "· Vanturis".
 */
export default function SEO({
  title,
  description,
  path,
  image,
  ogTitle,
  ogDescription,
  noIndex,
  robots: robotsOverride,   // optional verbatim robots directive (e.g. "noindex,follow")
  type = "website",
  exactTitle = false,
  jsonLd,
  heroPreload,   // { href, imagesrcset, imagesizes } — injects a
                 // `<link rel="preload" as="image">` in <head> so the
                 // browser starts fetching the LCP hero before React
                 // commits.  Only pass when the URL exactly matches
                 // what the rendered <img> will request.
}) {
  const manifest = path ? PAGE_BY_PATH[path] : null;
  // Normalize the incoming path for canonical use: drop query string /
  // hash, collapse trailing slashes (except for the root), and reject
  // any external origin that could sneak in from a caller passing a
  // full URL.  Canonical origin is always the Production host.
  const rawPath = path || "/";
  const cleanPath = (() => {
    let p = String(rawPath);
    // If a full URL was passed, extract only the pathname.
    if (/^https?:\/\//i.test(p)) {
      try { p = new URL(p).pathname; } catch (e) { p = "/"; }
    }
    // Drop query string and hash fragment.
    p = p.split("?")[0].split("#")[0];
    if (!p.startsWith("/")) p = `/${p}`;
    // Collapse trailing slash except at the root.
    if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");
    return p;
  })();
  const url = `${BASE}${cleanPath}`;
  // Default social share image — the owner-approved Vanturis canonical
  // social/link-share card (1200×630 JPEG, gold V + VANTURIS +
  // "MARKETPLACE · PASSPORT · AUCTIONS" on black).  Versioned filename
  // cache-busts old platform-cached previews.  Do NOT point at a
  // Preview / customer-assets URL.
  const ogImage = image || `${BASE}/social/vanturis-social-share-2026-luxury-travel.jpg?v=1`;

  const resolvedTitle = title || manifest?.title;
  const resolvedDescription = description || manifest?.description;

  const finalTitle = exactTitle
    ? (resolvedTitle || "Vanturis | Marketplace · Passport · Auction")
    : (resolvedTitle
        ? (resolvedTitle.endsWith("Vanturis") ? resolvedTitle : `${resolvedTitle} · Vanturis`)
        : "Vanturis · Luxury Motorcoach Marketplace & Bidding Platform");

  const finalDesc = resolvedDescription
    || "Vanturis is creating a trusted marketplace for exceptional luxury motorhomes, professional touring coaches and purpose-built tour trailers.";

  // Indexability precedence:  explicit prop > manifest.indexable > default noindex.
  const indexable = noIndex === false ? true
    : noIndex === true ? false
    : (manifest?.indexable === true && manifest?.status === "live");

  const robots = robotsOverride || (indexable ? "index,follow" : "noindex,nofollow");

  const ldBlocks = Array.isArray(jsonLd) ? jsonLd.filter(Boolean)
    : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />

      <meta property="og:site_name" content="Vanturis" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={ogTitle || finalTitle} />
      <meta property="og:description" content={ogDescription || finalDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || finalTitle} />
      <meta name="twitter:description" content={ogDescription || finalDesc} />
      <meta name="twitter:image" content={ogImage} />

      {ldBlocks.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}

      {heroPreload?.href && (
        <link
          rel="preload"
          as="image"
          href={heroPreload.href}
          imageSrcSet={heroPreload.imagesrcset || undefined}
          imageSizes={heroPreload.imagesizes || undefined}
          fetchPriority="high"
        />
      )}
    </Helmet>
  );
}
